import type {
	CartType,
	CheckoutQuoteType,
	CheckoutQuoteProblemType,
	CheckoutCurrency,
} from '@redux/types'
import { paymentApi, ResType } from '@api'
import { Decimal } from './Decimal'
import { checkoutAuthHeaders } from './CheckoutAuth'

/**
 * 장바구니 ↔ 서버 견적 (judahsWeb/CHECKOUT_PLAN.md §2, §5, §11, §13).
 *
 * 화면은 금액을 계산하지 않는다. 장바구니 줄을 견적 입력으로 바꾸고(`toQuoteItems`), 견적이 "지금은
 * 다르다"고 하면 줄을 고친다(`applyProblems`) — 재고가 모자라면 남은 만큼으로, 팔 수 없으면 줄을 뺀다.
 * **값이 바뀐 것은 여기서 자동으로 고치지 않는다** — 올랐으면 고객에게 묻고(`priceChanges` →
 * PriceChangeModal), 내렸으면 알리고 새 값으로 간다(`applyPriceDecision`).
 */
export interface CartFixResultType {
	cartList: CartType[]
	changed: boolean
	/** 장바구니를 고치게 만든 문제들 (값 변경·쿠폰·배송비 문제는 여기 없다) */
	lineProblems: CheckoutQuoteProblemType[]
	couponProblems: CheckoutQuoteProblemType[]
}

export interface PriceChangeType {
	seasonProductId: string
	productName: string
	optionName: string
	qty: number
	/** 고객이 본 단가(옵션 제외) */
	from: number
	/** 지금 단가(옵션 제외) */
	to: number
}

export interface PriceChangesType {
	currency: CheckoutCurrency
	increased: PriceChangeType[]
	decreased: PriceChangeType[]
}

/** 브라우저의 익명 카트 키. 서버가 카트 홀드·가격 잠금을 이 키로 잡는다 */
export const CartKey = {
	get: (): string => {
		try {
			const existing = localStorage.getItem('cartKey') ?? ''
			if (existing.length >= 6) return existing
			const created = `ck-${Date.now().toString(36)}-${Math.random()
				.toString(36)
				.slice(2, 12)}`
			localStorage.setItem('cartKey', created)
			return created
		} catch (error) {
			return `ck-session-${Date.now().toString(36)}`
		}
	},
}

/**
 * 장바구니 줄의 정체 = 시즌상품 × 옵션 (2026-09-17). 같은 제품이라도 옵션이 다르면 다른 줄이다 —
 * 서버 견적도 입력 항목마다 줄을 하나씩 돌려주고(checkoutQuote.service), 가격 잠금도
 * (시즌상품 × 옵션) 으로 잡는다. 화면의 React key · 수량 변경 · 삭제 · 견적 줄 조회는 전부 이 키로 한다.
 */
export const CartLineKey = {
	of: (anItem: { id: string; option?: { id: string } }): string =>
		`${anItem.id}|${anItem.option?.id ?? ''}`,
	is: (anItem: { id: string; option?: { id: string } }, key: string): boolean =>
		CartLineKey.of(anItem) === key,
}

/** 옵션 추가금 — `PRODUCT_OPTION.EXTRA_USD/KRW` 는 문자열로 온다 */
const extraUsd = (anItem: CartType): number => Number(anItem.option?.extraUsd ?? 0) || 0
const extraKrw = (anItem: CartType): number => Number(anItem.option?.extraKrw ?? 0) || 0

const seenUnit = (anItem: CartType, currency: CheckoutCurrency): number | undefined =>
	currency === 'KRW'
		? anItem.unitKrw !== undefined && anItem.unitKrw > 0
			? anItem.unitKrw
			: undefined
		: anItem.seasonUsd

export const CartQuote = {
	/**
	 * 견적 전에 화면이 잠깐 그리는 "담을 때 본 값" — 옵션 추가금 포함 단가. `seasonUsd`·`unitKrw` 는
	 * 옵션 제외 단가로 두어야 한다(서버가 `unitSeen` 을 옵션 제외 단가와 비교한다).
	 */
	localUnitUsd: (anItem: CartType): number =>
		Decimal(anItem.seasonUsd + extraUsd(anItem)),
	localUnitKrw: (anItem: CartType): number =>
		Math.round((anItem.unitKrw ?? 0) + extraKrw(anItem)),

	/** 장바구니 줄 → 견적 입력. `unitSeen` 은 담을 때 본 단가라 값이 바뀌었는지 서버가 알아본다 */
	toQuoteItems: (cartList: CartType[], currency: CheckoutCurrency) =>
		cartList.map(anItem => ({
			id: anItem.id,
			qty: anItem.qty,
			optionId: anItem.option?.id ?? '',
			unitSeen: seenUnit(anItem, currency),
			// 옛 서버가 읽는 필드도 함께 실어 둔다
			option: anItem.option,
			productCode: anItem.productCode,
			productName: anItem.productName,
			seasonId: anItem.seasonId,
			productId: anItem.productId,
		})),

	/** 값이 바뀐 줄 — 오른 것과 내린 것 */
	priceChanges: (cartList: CartType[], quote: CheckoutQuoteType): PriceChangesType => {
		const increased: PriceChangeType[] = []
		const decreased: PriceChangeType[] = []

		for (const anItem of cartList) {
			const line = CartQuote.line(quote, anItem.id, anItem.option?.id ?? '')
			if (line === undefined || line.productId === '') continue
			const from = seenUnit(anItem, quote.currency)
			if (from === undefined) continue
			const to = line.unitAmount
			if (to === from) continue

			// 값은 제품 단위로 바뀐다(옵션 제외 단가). 옵션이 여러 줄이면 한 건으로 묶고 수량만 합친다
			const bucket = to > from ? increased : decreased
			const existing = bucket.find(aChange => aChange.seasonProductId === anItem.id)
			if (existing !== undefined) {
				existing.qty += anItem.qty
				if (existing.optionName !== '') existing.optionName = ''
				continue
			}
			bucket.push({
				seasonProductId: anItem.id,
				productName: anItem.productName,
				optionName: anItem.option?.name ?? '',
				qty: anItem.qty,
				from,
				to,
			})
		}

		return { currency: quote.currency, increased, decreased }
	},

	/**
	 * 값 변경에 대한 고객의 결정을 장바구니에 적용한다. `keepIds` 에 없는 오른 줄은 뺀다.
	 * 남는 줄은 전부 지금 값으로 맞춘다(내린 값 포함).
	 */
	applyPriceDecision: (
		cartList: CartType[],
		quote: CheckoutQuoteType,
		changes: PriceChangesType,
		keepIds: string[],
	): CartType[] => {
		const dropped = new Set(
			changes.increased
				.filter(aChange => !keepIds.includes(aChange.seasonProductId))
				.map(aChange => aChange.seasonProductId),
		)

		return cartList
			.filter(anItem => !dropped.has(anItem.id))
			.map(anItem => {
				const line = CartQuote.line(quote, anItem.id, anItem.option?.id ?? '')
				if (line === undefined || line.productId === '') return anItem
				return {
					...anItem,
					seasonUsd: line.unitUsd,
					unitKrw: line.unitKrw,
					phase: line.phase,
					sumUsd: Decimal((line.unitUsd + line.optionUsd) * anItem.qty),
					vatUsd: 0,
				}
			})
	},

	/** 값 변경이 아닌 문제(품절·재고 부족·옵션·판매 불가)를 장바구니에 적용한다 */
	applyProblems: (cartList: CartType[], quote: CheckoutQuoteType): CartFixResultType => {
		const lineProblems = quote.problems.filter(
			aProblem => aProblem.seasonProductId && aProblem.code !== 'price_changed',
		)
		const couponProblems = quote.problems.filter(aProblem =>
			['coupon_invalid', 'coupon_not_applicable'].includes(aProblem.code),
		)

		if (lineProblems.length === 0) {
			return { cartList, changed: false, lineProblems, couponProblems }
		}

		const nextCartList: CartType[] = []
		/*
			문제는 제품(시즌상품) 단위로 온다. 같은 제품이 옵션별로 여러 줄이면 남은 수량(`expected`)을
			줄 순서대로 나눠 담고, 다 채워지면 뒤 줄은 뺀다.
		*/
		const remaining = new Map<string, number>()

		for (const anItem of cartList) {
			const problem = lineProblems.find(
				aProblem => aProblem.seasonProductId === anItem.id,
			)

			if (problem === undefined) {
				nextCartList.push(anItem)
				continue
			}

			if (problem.code === 'insufficient_qty') {
				if (!remaining.has(anItem.id)) {
					remaining.set(anItem.id, Math.max(0, Math.floor(problem.expected ?? 0)))
				}
				const left = remaining.get(anItem.id) ?? 0
				const nextQty = Math.min(anItem.qty, left)
				remaining.set(anItem.id, left - nextQty)
				if (nextQty > 0) {
					nextCartList.push({
						...anItem,
						qty: nextQty,
						sumUsd: Decimal(CartQuote.localUnitUsd(anItem) * nextQty),
					})
				}
			}
			// not_found · not_active · option_not_found → 줄을 뺀다
		}

		return { cartList: nextCartList, changed: true, lineProblems, couponProblems }
	},

	/** 고객에게 보여 줄 한 줄 — 단정문 (PERSONA §1) */
	describe: (aProblem: CheckoutQuoteProblemType, currency: CheckoutCurrency): string => {
		const name = aProblem.productName ? `${aProblem.productName}: ` : ''

		switch (aProblem.code) {
			case 'price_changed':
				return `${name}the price is now ${CartQuote.money(
					currency,
					aProblem.expected,
				)} (was ${CartQuote.money(currency, aProblem.seen)}).`
			case 'insufficient_qty':
				return aProblem.expected === 0
					? `${name}sold out. Removed from the cart.`
					: `${name}only ${aProblem.expected} left. The quantity was reduced.`
			case 'not_active':
				return `${name}${aProblem.message}. Removed from the cart.`
			case 'not_found':
				return `${name}no longer on sale. Removed from the cart.`
			case 'option_not_found':
				return `${name}the selected option is no longer available. Removed from the cart.`
			case 'coupon_invalid':
			case 'coupon_not_applicable':
				return aProblem.message
			case 'shipping_changed':
				return `The shipping fee is now ${CartQuote.money(currency, aProblem.expected)}.`
			default:
				return aProblem.message
		}
	},

	money: (currency: CheckoutCurrency, value: number | undefined): string =>
		value === undefined
			? ''
			: currency === 'KRW'
			? `₩${Math.round(value).toLocaleString('en-US')}`
			: `$${value.toLocaleString('en-US', {
					minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
					maximumFractionDigits: 2,
			  })}`,

	/** 견적 줄 찾기 — 화면이 단가·줄 합계를 그릴 때. 줄 = 시즌상품 × 옵션 */
	line: (quote: CheckoutQuoteType | null, seasonProductId: string, optionId = '') =>
		quote === null
			? undefined
			: quote.lines.find(
					aLine =>
						aLine.seasonProductId === seasonProductId && aLine.optionId === optionId,
			  ),

	/**
	 * 상시판매 카트 홀드를 서버와 맞춘다 (CHECKOUT_PLAN §11). 담을 때·수량 바꿀 때·뺄 때 부른다.
	 * 서버가 허락한 수량(`granted`)이 요청보다 작으면 줄의 수량을 그만큼으로 줄인다.
	 * 단계 판매 줄은 서버가 무시한다 — 점유하지 않는다.
	 */
	syncHolds: async (
		cartList: CartType[],
	): Promise<{ cartList: CartType[]; reduced: string[] }> => {
		const reduced: string[] = []

		if (cartList.length === 0) {
			try {
				await paymentApi.cartHold({
					body: { cartKey: CartKey.get(), items: [] },
					customHeaders: checkoutAuthHeaders(),
				})
			} catch (error) {
				// 홀드는 부가 장치다. 실패해도 장바구니는 그대로
			}
			return { cartList, reduced }
		}

		/*
			홀드는 제품(시즌상품) 단위다 — 서버 `syncCartHolds` 가 id 로 Map 을 만들어 같은 id 는
			마지막 항목만 남는다. 옵션이 여러 줄이면 수량을 합쳐 한 항목으로 보낸다.
		*/
		const wanted = new Map<string, number>()
		for (const anItem of cartList) {
			wanted.set(anItem.id, (wanted.get(anItem.id) ?? 0) + anItem.qty)
		}

		const apiRes: ResType | undefined = await paymentApi.cartHold({
			body: {
				cartKey: CartKey.get(),
				items: Array.from(wanted, ([id, qty]) => ({ id, qty })),
			},
			customHeaders: checkoutAuthHeaders(),
		})

		if (apiRes === undefined || ![200, 201].includes(apiRes.status)) {
			return { cartList, reduced }
		}

		const holds: {
			seasonProductId: string
			qty: number
			granted: number
			expiresAt: string
		}[] = apiRes.data[0]?.holds ?? []

		// 허락 수량을 같은 제품의 줄들에 순서대로 배분한다
		const granted = new Map<string, number>()
		for (const aHold of holds) granted.set(aHold.seasonProductId, aHold.granted)

		const nextCartList = cartList
			.map(anItem => {
				const hold = holds.find(aHold => aHold.seasonProductId === anItem.id)
				if (hold === undefined) return anItem
				const left = granted.get(anItem.id) ?? 0
				const nextQty = Math.min(anItem.qty, left)
				granted.set(anItem.id, left - nextQty)
				if (nextQty < anItem.qty) {
					const name =
						anItem.option !== undefined && anItem.option.id !== ''
							? `${anItem.productName} (${anItem.option.name})`
							: anItem.productName
					reduced.push(
						nextQty === 0
							? `${name}: no stock left. Removed from the cart.`
							: `${name}: only ${nextQty} left. The quantity was reduced.`,
					)
				}
				return {
					...anItem,
					qty: nextQty,
					sumUsd: Decimal(CartQuote.localUnitUsd(anItem) * nextQty),
					holdExpiresAt: hold.expiresAt,
				}
			})
			.filter(anItem => anItem.qty > 0)

		return { cartList: nextCartList, reduced }
	},
}
